import invert from 'lodash/invert';
import isFunction from 'lodash/isFunction';

import { ApiEndpoints, ApiStatusSuffixes, ApiToken } from '../constants/ApiActions';
import { GraphQL } from '../graphql';
import { Schemas } from '../store/Schemas';
import { type ApiEndpoint, type ApiSchema, type Inverted, type ValueOf } from '../types';

const invertedApiEndpoints = invert(ApiEndpoints) as Inverted<typeof ApiEndpoints>;

const ApiEndpointToSchemaMap = Object.freeze({
  [ApiEndpoints.ANON_LOGIN]: Schemas.LISTENER,
  [ApiEndpoints.LOGIN]: Schemas.LISTENER,
  [ApiEndpoints.DEVICE_LOGIN]: Schemas.LISTENER,
  [ApiEndpoints.LOGOUT]: Schemas.NO_ENTITY,
  [ApiEndpoints.LOG_OUT_ALL_DEVICES]: Schemas.NO_ENTITY,
  [ApiEndpoints.COMMUNITY_SSO]: Schemas.NO_ENTITY,
  [ApiEndpoints.FETCH_GENRE_CATEGORIES]: Schemas.GENRE_CATEGORIES_LIST,
  [ApiEndpoints.FETCH_GENRES_BY_CATEGORY_ID]: Schemas.GENRES_LIST,
  [ApiEndpoints.ACCOUNT_MESSAGE_DISMISSED]: Schemas.NO_ENTITY,
  [ApiEndpoints.FETCH_FULL_LYRICS]: Schemas.LYRIC,
  [ApiEndpoints.ADD_TIRED_SONG]: Schemas.NO_ENTITY,
  [ApiEndpoints.EMAIL_EXPLICIT_PIN]: Schemas.NO_ENTITY,
  [ApiEndpoints.EMAIL_PASSWORD]: Schemas.NO_ENTITY,
  [ApiEndpoints.GET_PROFILE]: Schemas.LISTENER,
  [ApiEndpoints.REGISTER]: Schemas.LISTENER,
  [ApiEndpoints.RESET_PASSWORD]: Schemas.NO_ENTITY,
  [ApiEndpoints.SET_PROFILE]: Schemas.LISTENER,
  [ApiEndpoints.SET_EXPLICIT_PIN]: Schemas.NO_ENTITY,
  [ApiEndpoints.UPDATE_ACCOUNT]: Schemas.LISTENER,
  [ApiEndpoints.ADD_TRACKING_CODE]: Schemas.NO_ENTITY,
  [ApiEndpoints.UPDATE_SETTINGS]: Schemas.LISTENER,
  [ApiEndpoints.VALIDATE_PASSWORD_RESET_TOKEN]: Schemas.NO_ENTITY,
  [ApiEndpoints.GET_FRAGMENT]: Schemas.FRAGMENT,
  [ApiEndpoints.FETCH_STATIONS]: Schemas.STATION_LIST,
  [ApiEndpoints.GET_OR_SET_SHUFFLE_STATIONS]: Schemas.STATION,
  [ApiEndpoints.CREATE_STATION]: Schemas.STATION,
  [ApiEndpoints.REMOVE_STATION]: Schemas.NO_ENTITY,
  [ApiEndpoints.UPDATE_STATION]: Schemas.NO_ENTITY,
  [ApiEndpoints.ADD_SEED]: Schemas.STATION,
  [ApiEndpoints.DELETE_SEED]: Schemas.STATION,
  [ApiEndpoints.FETCH_NARRATIVE]: Schemas.NO_ENTITY,
  [ApiEndpoints.ADD_FEEDBACK]: Schemas.NO_ENTITY,
  [ApiEndpoints.GET_FEEDBACK]: Schemas.FEEDBACK,
  [ApiEndpoints.DELETE_FEEDBACK]: Schemas.NO_ENTITY,
  [ApiEndpoints.INSERT_ARTIST_MESSAGE_METRIC]: Schemas.NO_ENTITY,
  [ApiEndpoints.INSERT_VOICE_TRACK_METRIC]: Schemas.NO_ENTITY,
  [ApiEndpoints.FLAG_MESSAGE]: Schemas.NO_ENTITY,
  [ApiEndpoints.UPDATE_SHUFFLE_STATION]: Schemas.STATION,
  [ApiEndpoints.VERIFY_HYBRID_STATION_CHECKSUM]: Schemas.NO_ENTITY,
  [ApiEndpoints.TRACK_STARTED]: Schemas.NO_ENTITY,
  [ApiEndpoints.PLAYBACK_PAUSED]: Schemas.NO_ENTITY,
  [ApiEndpoints.PLAYBACK_RESUMED]: Schemas.NO_ENTITY,
  [ApiEndpoints.ARTIST_SHARE]: Schemas.NO_ENTITY,
  [ApiEndpoints.SEARCH]: Schemas.NO_ENTITY,
  [ApiEndpoints.SEARCH_AUTOCOMPLETE]: Schemas.NO_ENTITY,
  [ApiEndpoints.SEARCH_RECOMMENDATIONS]: Schemas.SEARCH_RECOMMENDATIONS,
  [ApiEndpoints.GET_AVAILABLE_PRODUCTS]: Schemas.NO_ENTITY,
  [ApiEndpoints.GET_SPECIAL_OFFER_V2]: Schemas.NO_ENTITY,
  [ApiEndpoints.GET_ADLIST]: Schemas.NO_ENTITY,
  [ApiEndpoints.USE_SKIP_REWARD]: Schemas.NO_ENTITY,
  [ApiEndpoints.USE_REPLAY_REWARD]: Schemas.NO_ENTITY,
  [ApiEndpoints.START_VALUE_EXCHANGE]: Schemas.NO_ENTITY,
  [ApiEndpoints.FACEBOOK_CONNECT]: Schemas.NO_ENTITY,
  [ApiEndpoints.FACEBOOK_DISCONNECT]: Schemas.NO_ENTITY,
  [ApiEndpoints.GET_REPLAY_TRACK]: Schemas.REPLAY_TRACK,
  [ApiEndpoints.SET_TWITTER_TOKENS]: Schemas.NO_ENTITY,
  [ApiEndpoints.GET_REQUEST_TOKEN]: Schemas.NO_ENTITY,
  [ApiEndpoints.GET_AUTH_TOKEN]: Schemas.NO_ENTITY,
  [ApiEndpoints.POST_STATUS_UPDATE]: Schemas.NO_ENTITY,
  [ApiEndpoints.REGISTER_IMPRESSION]: Schemas.NO_ENTITY,
  [ApiEndpoints.REGISTER_VIDEO_AD_IMPRESSION]: Schemas.NO_ENTITY,
  [ApiEndpoints.REGISTER_AUDIO_PROGRAMMATIC_IMPRESSION]: Schemas.NO_ENTITY,
  [ApiEndpoints.SEND_STATS_EVENTS]: Schemas.NO_ENTITY,
  [ApiEndpoints.SEND_NEW_SITE_FEEDBACK]: Schemas.NO_ENTITY,
  [ApiEndpoints.DELETE_LISTENER]: Schemas.LISTENER,
  [ApiEndpoints.GET_SEED_SUGGESTIONS]: Schemas.SEED_SUGGESTIONS,
  [ApiEndpoints.GET_STATION_DETAILS]: Schemas.STATION,
  [ApiEndpoints.GET_SEEDS]: Schemas.SEED_LIST,
  [ApiEndpoints.GET_STATION_FEEDBACK]: Schemas.FEEDBACK,
  [ApiEndpoints.BROKEN_AD]: Schemas.NO_ENTITY,
  [ApiEndpoints.ACQUIRE_PRODUCT]: Schemas.LISTENER,
  [ApiEndpoints.PREPARE_TO_ACQUIRE_PAYPAL_PRODUCT]: Schemas.NO_ENTITY,
  [ApiEndpoints.GET_SUBSCRIPTION_INFO_V2]: Schemas.NO_ENTITY,
  [ApiEndpoints.UPDATE_BILLING_INFO_V2]: Schemas.NO_ENTITY,
  [ApiEndpoints.GET_CREDIT_CARD_V2]: Schemas.NO_ENTITY,
  [ApiEndpoints.CANCEL_CURRENT_PRODUCT]: Schemas.NO_ENTITY,
  [ApiEndpoints.GET_EXPERIMENTS_FOR_LISTENER]: Schemas.NO_ENTITY,

  [ApiEndpoints.CATALOG_ANNOTATE_OBJECTS]: Schemas.NO_ENTITY,
  [ApiEndpoints.CATALOG_ANNOTATE_OBJECTS_SIMPLE]: Schemas.NO_ENTITY,
  [ApiEndpoints.CATALOG_GET_DETAILS]: Schemas.NO_ENTITY,
  [ApiEndpoints.CATALOG_GET_ARTIST_DISCOGRAPHY]: Schemas.NO_ENTITY,
  [ApiEndpoints.GET_ARTIST_EVENTS]: Schemas.NO_ENTITY,
  [ApiEndpoints.CATALOG_GET_ALL_ARTIST_TRACKS]: Schemas.NO_ENTITY,
  [ApiEndpoints.CATALOG_GET_DETAILS_WITH_COLLABORATIONS]: Schemas.NO_ENTITY,
  [ApiEndpoints.CATALOG_GET_ALL_ARTIST_TRACKS_WITH_COLLABORATIONS]: Schemas.NO_ENTITY,
  [ApiEndpoints.CATALOG_GET_ARTIST_DISCOGRAPHY_WITH_COLLABORATIONS]: Schemas.NO_ENTITY,
  [ApiEndpoints.CATALOG_SORT_OBJECTS]: Schemas.NO_ENTITY,
  [ApiEndpoints.COLLECTIONS_GET_VERSION]: Schemas.NO_ENTITY,
  [ApiEndpoints.COLLECTIONS_GET_ITEMS]: Schemas.NO_ENTITY,
  [ApiEndpoints.COLLECTIONS_GET_ITEMS_BY_ARTIST]: Schemas.NO_ENTITY,
  [ApiEndpoints.COLLECTIONS_GET_SORTED_BY_TYPES]: Schemas.NO_ENTITY,
  [ApiEndpoints.COLLECTIONS_GET_SORTED_BY_TYPES_V6]: Schemas.NO_ENTITY,
  [ApiEndpoints.COLLECTIONS_GET_SORTED_PLAYLISTS]: Schemas.NO_ENTITY,
  [ApiEndpoints.COLLECTIONS_ADD_ITEM]: Schemas.NO_ENTITY,
  [ApiEndpoints.COLLECTIONS_REMOVE_ITEM]: Schemas.NO_ENTITY,
  [ApiEndpoints.PLAYLISTS_CREATE]: Schemas.NO_ENTITY,
  [ApiEndpoints.PLAYLISTS_ANNOTATE_PLAYLISTS]: Schemas.NO_ENTITY,
  [ApiEndpoints.PLAYLISTS_GET_TRACKS]: Schemas.PLAYLIST,
  [ApiEndpoints.PLAYLISTS_EDIT_TRACKS]: Schemas.NO_ENTITY,
  [ApiEndpoints.PLAYLISTS_APPEND_ITEMS]: Schemas.NO_ENTITY,
  [ApiEndpoints.PLAYLISTS_DELETE_TRACKS]: Schemas.NO_ENTITY,
  [ApiEndpoints.PLAYLISTS_SET_DETAILS]: Schemas.NO_ENTITY,
  [ApiEndpoints.SOD_SEARCH]: Schemas.NO_ENTITY,
  [ApiEndpoints.PODS_GET_PLAYLIST_AUTOFILL_SONGS]: Schemas.NO_ENTITY,
  [ApiEndpoints.PODS_GET_PLAYLIST_SEARCH_RECOMMENDATIONS]: Schemas.NO_ENTITY,

  // Browse
  [ApiEndpoints.GET_MUSIC_CATALOG]: Schemas.BROWSE_MUSIC_CATALOG,

  // Family Plan
  [ApiEndpoints.FAMILY_PLAN_GET_RECORD]: Schemas.NO_ENTITY,
  [ApiEndpoints.FAMILY_PLAN_GET_PARENT_INFO]: Schemas.NO_ENTITY,
  [ApiEndpoints.FAMILY_PLAN_CREATE_INVITE]: Schemas.NO_ENTITY,
  [ApiEndpoints.FAMILY_PLAN_RESEND_INVITE]: Schemas.NO_ENTITY,
  [ApiEndpoints.FAMILY_PLAN_ACCEPT_INVITE]: Schemas.NO_ENTITY,
  [ApiEndpoints.FAMILY_PLAN_REVOKE_INVITE]: Schemas.NO_ENTITY,
  [ApiEndpoints.FAMILY_PLAN_REMOVE_CHILD]: Schemas.NO_ENTITY,
  [ApiEndpoints.FAMILY_PLAN_EXIT]: Schemas.NO_ENTITY,

  // Student/Military
  [ApiEndpoints.GET_DISCOUNT_VERIFICATION_INFO]: Schemas.NO_ENTITY,
  [ApiEndpoints.SUBMIT_DISCOUNT_VERIFICATION]: Schemas.NO_ENTITY,

  // APS
  [ApiEndpoints.ACTION_MODE]: Schemas.NO_ENTITY,
  [ApiEndpoints.ACTION_PAUSE]: Schemas.NO_ENTITY,
  [ApiEndpoints.ACTION_PREVIOUS]: Schemas.NO_ENTITY,
  [ApiEndpoints.ACTION_REMOVE_THUMB]: Schemas.NO_ENTITY,
  [ApiEndpoints.ACTION_REPEAT]: Schemas.NO_ENTITY,
  [ApiEndpoints.ACTION_REPLAY]: Schemas.NO_ENTITY,
  [ApiEndpoints.ACTION_SHUFFLE]: Schemas.NO_ENTITY,
  [ApiEndpoints.ACTION_SKIP]: Schemas.NO_ENTITY,
  [ApiEndpoints.ACTION_SNOOZE]: Schemas.NO_ENTITY,
  [ApiEndpoints.ACTION_THUMB_DOWN]: Schemas.NO_ENTITY,
  [ApiEndpoints.ACTION_THUMB_UP]: Schemas.NO_ENTITY,
  [ApiEndpoints.EVENT_ENDED]: Schemas.NO_ENTITY,
  [ApiEndpoints.EVENT_PROGRESS]: Schemas.NO_ENTITY,
  [ApiEndpoints.EVENT_STARTED]: Schemas.NO_ENTITY,
  [ApiEndpoints.PLAYBACK_CURRENT]: Schemas.NO_ENTITY,
  [ApiEndpoints.PLAYBACK_PEEK]: Schemas.NO_ENTITY,
  [ApiEndpoints.PLAYBACK_SOURCE]: Schemas.NO_ENTITY,

  // Discovery Tuner
  [ApiEndpoints.INTERACTIVE_RADIO_SET_MODE]: Schemas.NO_ENTITY,
  [ApiEndpoints.INTERACTIVE_RADIO_GET_MODES]: Schemas.NO_ENTITY,

  // Aesop
  [ApiEndpoints.AESOP_GET_DETAILS]: Schemas.NO_ENTITY,
  [ApiEndpoints.AESOP_GET_ALL_THUMBED_EPISODES_BY_PROGRAM]: Schemas.NO_ENTITY,
  [ApiEndpoints.AESOP_GET_ALL_EPISODES_BY_PROGRAM]: Schemas.NO_ENTITY,
  [ApiEndpoints.AESOP_ANNOTATE_OBJECTS]: Schemas.NO_ENTITY,
  [ApiEndpoints.AESOP_REMOVE_EPISODES_FEEDBACK]: Schemas.NO_ENTITY,

  // PLS
  [ApiEndpoints.PLS_GET_DIRECTORY]: Schemas.NO_ENTITY,

  // Sheer ID
  [ApiEndpoints.DISCOUNT_ORG_SEARCH_AUTOCOMPLETE]: Schemas.NO_ENTITY,

  // GraphQL
  [ApiEndpoints.GRAPHQL]: Schemas.NO_ENTITY,

  // APS Display Ads
  [ApiEndpoints.ADS_DISPLAY_AD]: Schemas.NO_ENTITY,

  // APS rewards
  [ApiEndpoints.CREATE_REWARD]: Schemas.NO_ENTITY,
} as Record<ApiEndpoint, ApiSchema>);

// ACTION_ATTEMPT -> ACTION; ACTION_SUCCESS -> ACTION
export function parseRequestAction(action: $TSFixMe): string {
  for (const suffix of ApiStatusSuffixes) {
    if (action.endsWith(`_${suffix}`)) {
      return action.substring(0, action.length - suffix.length - 1); // -1 for the _
    }
  }
  throw new Error('Not a request action');
}

export function getActionTypesForPrefix(actionPrefix: keyof typeof ApiEndpoints): string[] {
  if (!ApiEndpoints[actionPrefix]) {
    throw new Error('Api actionPrefix, "${actionPrefix}" is not defined');
  }

  return ApiStatusSuffixes.map(suffix => `${actionPrefix}_${suffix}`);
}
export const getActionPrefix = <Endpoint extends ValueOf<typeof ApiEndpoints>>(
  endpoint: Endpoint,
): typeof invertedApiEndpoints[Endpoint] => invertedApiEndpoints[endpoint];

export function getApiActionTypes(requestParams: $TSFixMe): $TSFixMe {
  const { endpoint, serviceEndpoint, graphQLOperationKeyName } = requestParams;

  if (graphQLOperationKeyName) {
    return GraphQL.Utils.getGraphQLActionTypes(graphQLOperationKeyName);
  }

  const actionPrefix = getActionPrefix(endpoint || serviceEndpoint);
  return getActionTypesForPrefix(actionPrefix);
}

interface IRequestObject {
  checker?: null | $TSFixMeFunction;
  endpoint: ValueOf<typeof ApiEndpoints>;
}
export function isRequestInFlight(
  state: $TSFixMe,
  { endpoint, checker = null }: IRequestObject,
): $TSFixMe {
  const requestAction = getActionPrefix(endpoint);
  const inFlightRequests = state.api.get('IN_FLIGHT');
  return inFlightRequests.find((candidateInFlight: $TSFixMe) => {
    const match = candidateInFlight.requestAction === requestAction;
    if (match && isFunction(checker)) {
      return checker(candidateInFlight);
    }
    return match;
  });
}

export function getSchemaByEndpoint<Endpoint extends ApiEndpoint>(
  endpoint: Endpoint,
): typeof ApiEndpointToSchemaMap[Endpoint] {
  const schema = ApiEndpointToSchemaMap[endpoint];
  if (!schema) {
    throw new Error(`No "schema" found for API endpoint: ${endpoint}.`);
  }
  return schema;
}

export function getSchema(requestParams: $TSFixMe = {}): $TSFixMe {
  const { endpoint, serviceEndpoint, graphQLOperationKeyName } = requestParams;

  if (graphQLOperationKeyName) {
    return GraphQL.Utils.getSchemaByGraphQLOperationKeyName(graphQLOperationKeyName);
  }

  return getSchemaByEndpoint(endpoint || serviceEndpoint);
}

export function isCatalogAnnotationEndpoint(endpoint: ValueOf<typeof ApiEndpoints>): boolean {
  return endpoint.indexOf(ApiEndpoints.CATALOG_ANNOTATE_OBJECTS) === 0;
}

export function isCatalogDetailsEndpoint(endpoint: ValueOf<typeof ApiEndpoints>): boolean {
  return endpoint.indexOf(ApiEndpoints.CATALOG_GET_DETAILS) === 0;
}

export function isBrowseGetDirectoryEndpoint(endpoint: ValueOf<typeof ApiEndpoints>): boolean {
  return endpoint.indexOf(ApiEndpoints.PLS_GET_DIRECTORY) === 0;
}

export function isLoginStyleAction(action: $TSFixMe): boolean {
  return (
    action[ApiToken] &&
    ([ApiEndpoints.LOGIN].indexOf(action[ApiToken].endpoint) !== -1 ||
      [ApiEndpoints.ANON_LOGIN].indexOf(action[ApiToken].endpoint) !== -1)
  );
}

export function isAuthLoginEndpoint(endpoint: ValueOf<typeof ApiEndpoints>): boolean {
  return endpoint.indexOf(ApiEndpoints.LOGIN) === 0;
}

// %%HACK%% The backend services may not always have catalog item tokens in sync with each other.
// As a fallback, we store a map of the request token to the musicId.
// See WEBCA-1156
export function getGenreMusicIdByUrlSlug(state: $TSFixMe, urlSlug: $TSFixMe): $TSFixMe {
  return state.api.getIn(['GENRE_REQUEST_TOKEN_MAP', urlSlug]);
}
